import { create } from "zustand";
import type {
  Course,
  CourseListItem,
  Assignment,
  AssignmentListItem,
  CourseMaterialListItem,
  AssignmentType,
} from "../types/homework";
import { api } from "../lib/api";

interface HomeworkState {
  courses: CourseListItem[];
  currentCourse: Course | null;
  assignments: AssignmentListItem[];
  currentAssignment: Assignment | null;
  materials: CourseMaterialListItem[];
  isLoading: boolean;
  fetchError: string | null;

  fetchCourses: () => Promise<void>;
  createCourse: (
    name: string,
    description?: string,
    instructor?: string,
    term?: string,
  ) => Promise<Course>;
  fetchCourse: (courseId: string) => Promise<void>;
  updateCourse: (courseId: string, data: Partial<Course>) => Promise<void>;
  deleteCourse: (courseId: string) => Promise<void>;

  fetchAssignments: (courseId: string) => Promise<void>;
  createAssignment: (
    courseId: string,
    title: string,
    opts?: {
      description?: string;
      assignment_type?: AssignmentType;
      due_date?: string;
      requirements_raw?: string;
    },
  ) => Promise<Assignment>;
  fetchAssignment: (assignmentId: string) => Promise<void>;
  startAssignment: (assignmentId: string) => Promise<Assignment>;
  completeAssignment: (assignmentId: string) => Promise<void>;
  reopenAssignment: (assignmentId: string) => Promise<void>;
  deleteAssignment: (courseId: string, assignmentId: string) => Promise<void>;

  fetchMaterials: (courseId: string) => Promise<void>;
  uploadMaterial: (
    courseId: string,
    files: File[],
    materialType: string,
  ) => Promise<void>;
  deleteMaterial: (courseId: string, materialId: string) => Promise<void>;
  parseSyllabus: (courseId: string) => Promise<void>;
}

let _coursesFetchPromise: Promise<void> | null = null;

export const useHomeworkStore = create<HomeworkState>()((set, get) => ({
  courses: [],
  currentCourse: null,
  assignments: [],
  currentAssignment: null,
  materials: [],
  isLoading: false,
  fetchError: null,

  fetchCourses: async () => {
    if (_coursesFetchPromise) return _coursesFetchPromise;
    set({ isLoading: true, fetchError: null });
    _coursesFetchPromise = (async () => {
      try {
        const courses = await api.get<CourseListItem[]>("/api/v1/courses");
        set({ courses });
      } catch {
        set({ fetchError: "Could not connect to the server. Make sure the backend is running." });
      } finally {
        set({ isLoading: false });
        _coursesFetchPromise = null;
      }
    })();
    return _coursesFetchPromise;
  },

  createCourse: async (name, description, instructor, term) => {
    try {
      const course = await api.post<Course>("/api/v1/courses", {
        name,
        description,
        instructor,
        term,
      });
      set((s) => ({ courses: [{ ...course }, ...s.courses] }));
      return course;
    } catch (err) {
      set({ fetchError: "Failed to create course. Please try again." });
      throw err;
    }
  },

  fetchCourse: async (courseId) => {
    set({ isLoading: true, fetchError: null });
    try {
      const course = await api.get<Course>(`/api/v1/courses/${courseId}`);
      set({ currentCourse: course });
    } catch {
      set({ fetchError: "Could not load this course. Make sure the backend is running." });
    } finally {
      set({ isLoading: false });
    }
  },

  updateCourse: async (courseId, data) => {
    try {
      const course = await api.put<Course>(`/api/v1/courses/${courseId}`, data);
      set({ currentCourse: course });
    } catch (err) {
      set({ fetchError: "Failed to update course." });
      throw err;
    }
  },

  deleteCourse: async (courseId) => {
    try {
      await api.delete(`/api/v1/courses/${courseId}`);
      set((s) => ({
        courses: s.courses.filter((c) => c.id !== courseId),
        currentCourse:
          s.currentCourse?.id === courseId ? null : s.currentCourse,
      }));
    } catch (err) {
      set({ fetchError: "Failed to delete course." });
      throw err;
    }
  },

  fetchAssignments: async (courseId) => {
    try {
      const assignments = await api.get<AssignmentListItem[]>(
        `/api/v1/courses/${courseId}/assignments`,
      );
      set({ assignments });
    } catch {
      set((s) => ({
        fetchError: s.fetchError || "Could not load assignments.",
      }));
    }
  },

  createAssignment: async (courseId, title, opts = {}) => {
    try {
      const assignment = await api.post<Assignment>(
        `/api/v1/courses/${courseId}/assignments`,
        { title, ...opts },
      );
      set((s) => ({ assignments: [...s.assignments, assignment] }));
      return assignment;
    } catch (err) {
      set({ fetchError: "Failed to create assignment. Please try again." });
      throw err;
    }
  },

  fetchAssignment: async (assignmentId) => {
    try {
      const assignment = await api.get<Assignment>(
        `/api/v1/assignments/${assignmentId}`,
      );
      set({ currentAssignment: assignment });
    } catch {
      set({ fetchError: "Could not load assignment." });
    }
  },

  startAssignment: async (assignmentId) => {
    try {
      const assignment = await api.post<Assignment>(
        `/api/v1/assignments/${assignmentId}/start`,
      );
      set((s) => ({
        currentAssignment: assignment,
        assignments: s.assignments.map((a) =>
          a.id === assignmentId ? { ...a, ...assignment } : a,
        ),
      }));
      return assignment;
    } catch (err) {
      set({ fetchError: "Failed to start assignment. Please try again." });
      throw err;
    }
  },

  completeAssignment: async (assignmentId) => {
    try {
      const assignment = await api.post<Assignment>(
        `/api/v1/assignments/${assignmentId}/complete`,
      );
      set((s) => ({
        currentAssignment: assignment,
        assignments: s.assignments.map((a) =>
          a.id === assignmentId
            ? { ...a, status: "completed" as const }
            : a,
        ),
      }));
    } catch (err) {
      set({ fetchError: "Failed to complete assignment." });
      throw err;
    }
  },

  reopenAssignment: async (assignmentId) => {
    try {
      const assignment = await api.put<Assignment>(
        `/api/v1/assignments/${assignmentId}`,
        { status: "in_progress" },
      );
      set((s) => ({
        currentAssignment:
          s.currentAssignment?.id === assignmentId ? assignment : s.currentAssignment,
        assignments: s.assignments.map((a) =>
          a.id === assignmentId
            ? { ...a, status: "in_progress" as const }
            : a,
        ),
      }));
    } catch (err) {
      set({ fetchError: "Failed to reopen assignment." });
      throw err;
    }
  },

  deleteAssignment: async (_courseId, assignmentId) => {
    try {
      await api.delete(`/api/v1/assignments/${assignmentId}`);
      set((s) => ({
        assignments: s.assignments.filter((a) => a.id !== assignmentId),
        currentAssignment:
          s.currentAssignment?.id === assignmentId ? null : s.currentAssignment,
      }));
    } catch (err) {
      set({ fetchError: "Failed to delete assignment." });
      throw err;
    }
  },

  fetchMaterials: async (courseId) => {
    try {
      const materials = await api.get<CourseMaterialListItem[]>(
        `/api/v1/courses/${courseId}/materials`,
      );
      set({ materials });
    } catch {
      set({ fetchError: "Could not load materials." });
    }
  },

  uploadMaterial: async (courseId, files, materialType) => {
    try {
      const formData = new FormData();
      for (const file of files) {
        formData.append("file", file);
      }
      formData.append("material_type", materialType);

      await api.upload(`/api/v1/courses/${courseId}/materials`, formData);

      await get().fetchMaterials(courseId);
      if (materialType === "syllabus") {
        await get().fetchCourse(courseId);
      }
    } catch (err) {
      set({ fetchError: "Failed to upload material. Please try again." });
      throw err;
    }
  },

  deleteMaterial: async (courseId, materialId) => {
    try {
      await api.delete(`/api/v1/courses/${courseId}/materials/${materialId}`);
      set((s) => ({
        materials: s.materials.filter((m) => m.id !== materialId),
      }));
    } catch {
      set({ fetchError: "Failed to delete material." });
    }
  },

  parseSyllabus: async (courseId) => {
    set({ isLoading: true });
    try {
      await api.post(`/api/v1/courses/${courseId}/parse-syllabus`);
      await get().fetchCourse(courseId);
    } catch {
      set({ fetchError: "Failed to parse syllabus." });
    } finally {
      set({ isLoading: false });
    }
  },
}));
